/**
 * Lightweight fetch wrapper that:
 *   - always sends/keeps cookies (Better Auth session)
 *   - stringifies JSON body
 *   - throws on non-2xx with the parsed error
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...((headers as Record<string, string>) ?? {}),
  };
  let body: BodyInit | undefined = init.body as BodyInit | undefined;
  if (json !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  }
  const res = await fetch(path, {
    credentials: 'include',
    ...rest,
    headers: finalHeaders,
    body,
  });
  const text = await res.text();
  const parsed = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new ApiError(
      typeof parsed === 'object' && parsed && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `Request failed with ${res.status}`,
      res.status,
      parsed,
    );
    throw err;
  }
  return parsed as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public payload: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
